<template>
	<Skeleton :paragraph="{ rows: 15 }" :title="false" :loading="loading">
		<VerticalLayout>
			<Input
				v-model:value="searchQuery"
				class="width-100"
				allowClear
				:placeholder="$t('settings.sub-networks.baseStationsForm.searchPlaceholder')"
			/>
			<div class="mover-group-wrapper scrollable-x">
				<div>
					<div class="checkbox-group-title">{{ $t('settings.sub-networks.baseStationsForm.selectedTitle') }}</div>
					<CheckboxesComponent v-model:value="selectedBaseStationsCheckboxes" :disabled="settings.deleted" bordered>
						<Checkbox
							v-for="baseStation in selectedBaseStationsDisplay"
							class="nobr"
							:key="baseStation.id"
							:value="baseStation.id"
						>
							<TypographyText strong>{{ baseStation.mntpnt_int }}</TypographyText> | {{ baseStation.label }}
							<Tooltip :title="$t('settings.sub-networks.baseStationsForm.includeInVrsTooltip.' + (baseStation.include_in_vrs ? 'enabled' : 'disabled'))">
								<Tag class="include-in-vrs-tag" :color="baseStation.include_in_vrs ? 'success' : 'default'">{{ $t('settings.sub-networks.baseStationsForm.includeInVrsValue.' + (baseStation.include_in_vrs ? 'enabled' : 'disabled')) }}</Tag>
							</Tooltip>
						</Checkbox>
					</CheckboxesComponent>
					<Checkbox class="nobr select-all-checkbox" v-model:checked="selectAllChecked1">
						{{ $t('settings.sub-networks.baseStationsForm.selectAllCheckboxLabel') }}
					</Checkbox>
				</div>
				<VerticalLayout class="buttons-wrapper">
					<Button
						type="default"
						class="width-100"
						:disabled="isSubmitting || settings.deleted || nonSelectedBaseStationsCheckboxes.length === 0"
						@click="onSelect"
					>
						<ArrowLeftOutlined />
						{{ $t('settings.sub-networks.baseStationsForm.selectButtonLabel', nonSelectedBaseStationsCheckboxes.length) }}
					</Button>
					<Button
						type="default"
						class="width-100"
						:disabled="isSubmitting || settings.deleted || selectedBaseStationsCheckboxes.length === 0"
						@click="onDeselect"
					>
						{{ $t('settings.sub-networks.baseStationsForm.deselectButtonLabel', selectedBaseStationsCheckboxes.length) }}
						<ArrowRightOutlined />
					</Button>
				</VerticalLayout>
				<div>
					<div class="checkbox-group-title">{{ $t('settings.sub-networks.baseStationsForm.nonSelectedTitle') }}</div>
					<CheckboxesComponent v-model:value="nonSelectedBaseStationsCheckboxes" :disabled="settings.deleted" bordered>
						<Checkbox
							v-for="baseStation in nonSelectedBaseStationsDisplay"
							:key="baseStation.id"
							:value="baseStation.id"
							class="nobr"
						>
							<TypographyText strong>{{ baseStation.mntpnt_int }}</TypographyText> | {{ baseStation.label }}
							<Tooltip :title="$t('settings.sub-networks.baseStationsForm.includeInVrsTooltip.' + (baseStation.include_in_vrs ? 'enabled' : 'disabled'))">
								<Tag class="include-in-vrs-tag" :color="baseStation.include_in_vrs ? 'success' : 'default'">{{ $t('settings.sub-networks.baseStationsForm.includeInVrsValue.' + (baseStation.include_in_vrs ? 'enabled' : 'disabled')) }}</Tag>
							</Tooltip>
						</Checkbox>
					</CheckboxesComponent>
					<Checkbox class="nobr select-all-checkbox" v-model:checked="selectAllChecked2">
						{{ $t('settings.sub-networks.baseStationsForm.selectAllCheckboxLabel') }}
					</Checkbox>
				</div>
			</div>
			<Space>
				<Button type="primary" @click="onSubmit" :loading="isSubmitting" :disabled="settings.deleted">
					{{ $t('settings.sub-networks.baseStationsForm.buttonLabel') }}
				</Button>
				<TypographyText>
					{{ $t('settings.sub-networks.baseStationsForm.selectedLabel', selectedBaseStationsCount) }}
				</TypographyText>
			</Space>
		</VerticalLayout>
	</Skeleton>
</template>

<script>
import { toRef, ref, computed, watch, nextTick } from 'vue';
import { TypographyText, Skeleton, Space, Button, Input, Checkbox, Tag, Tooltip, message } from 'ant-design-vue';
import { ArrowLeftOutlined, ArrowRightOutlined } from '@ant-design/icons-vue';

import Utils from '@/api/Utils';

import { useI18n } from 'vue-i18n'

import Validators from '@/api/Validators';

import VerticalLayout from '@/components/common/VerticalLayout';
import CheckboxesComponent from '@/components/common/CheckboxesComponent';

import { useBaseStationsStore } from '@/stores/baseStations';

import { useProvideInjectFormChangedStatuses } from '@/utils/useProvideInject';


export default {
	components: {
		TypographyText, Skeleton, Space, Button, Input, Checkbox, Tag, Tooltip,
		ArrowLeftOutlined, ArrowRightOutlined,
		VerticalLayout, CheckboxesComponent,
	},
	props: ['loading', 'status', 'settings', 'onUpdateField', 'fetchSettings'],
	setup(props) {
		const { t } = useI18n();
		const selectedBaseStations = ref([]);
		const searchQuery = ref('');

		const { injectValue } = useProvideInjectFormChangedStatuses;
		const formChangedStatuses = injectValue();
		const { enableWatch, pauseWatch, resetChangedState } = Validators.useWatchFormChanges(
			selectedBaseStations,
			formChangedStatuses,
			'BaseStationsForm',
		);

		const settingsRef = toRef(props, 'settings');
		watch(settingsRef, value => {
			pauseWatch();
			selectedBaseStations.value = value.baseStations;
			nextTick(enableWatch);
		});

		const isSubmitting = ref(false);
		const onSubmit = () => {
			isSubmitting.value = true;
			props.onUpdateField('base_stations', selectedBaseStations.value, response => {
				isSubmitting.value = false;
				// вариант с errors обработан в onUpdateField
				if (response.status === 'ok') {
					resetChangedState();
					if (response.warning) {
						// это не ошибка, а предупреждение
						// пока используем, чтобы уведомить пользователя, что подсеть остановлена, когда он сделал мало БС
						message.warning(response.warning);
						props.fetchSettings();
					} else {
						message.success(t('settings.vrsSettingsForm.messages.success'));
					}
				}
			});
		}

		const baseStationsStore = useBaseStationsStore();
		const baseStations = computed(() => Utils.sortArray(
			baseStationsStore.items.filter(baseStation => {
				const query = searchQuery.value.toLowerCase().trim();
				return baseStation.label.toLowerCase().includes(query) || baseStation.mntpnt_int.toLowerCase().includes(query);
			}),
			Utils.objectFieldMultiSorter('mntpnt_int'),
		));
		const selectedBaseStationsCheckboxes = ref([]);
		const selectedBaseStationsDisplay = computed(() => {
			return baseStations.value.filter(baseStation => selectedBaseStations.value.includes(baseStation.id));
		});
		const nonSelectedBaseStationsCheckboxes = ref([]);
		const nonSelectedBaseStationsDisplay = computed(() => {
			return baseStations.value.filter(baseStation => !selectedBaseStations.value.includes(baseStation.id));
		});

		const selectAllChecked1 = ref(false);
		const selectAllChecked2 = ref(false);
		watch(selectAllChecked1, value => {
			if (value) {
				selectedBaseStationsCheckboxes.value = selectedBaseStationsDisplay.value.map(baseStation => baseStation.id);
			} else {
				selectedBaseStationsCheckboxes.value = [];
			}
		});
		watch(selectAllChecked2, value => {
			if (value) {
				nonSelectedBaseStationsCheckboxes.value = nonSelectedBaseStationsDisplay.value.map(baseStation => baseStation.id);
			} else {
				nonSelectedBaseStationsCheckboxes.value = [];
			}
		});

		const onSelect = () => {
			selectedBaseStations.value = [...selectedBaseStations.value, ...nonSelectedBaseStationsCheckboxes.value];
			selectAllChecked1.value = false;
			selectAllChecked2.value = false;
			selectedBaseStationsCheckboxes.value = [];
			nonSelectedBaseStationsCheckboxes.value = [];
		}
		const onDeselect = () => {
			selectedBaseStations.value = selectedBaseStations.value.filter(baseStationId => !selectedBaseStationsCheckboxes.value.includes(baseStationId));
			selectAllChecked1.value = false;
			selectAllChecked2.value = false;
			selectedBaseStationsCheckboxes.value = [];
			nonSelectedBaseStationsCheckboxes.value = [];
		}
		const selectedBaseStationsCount = computed(() => selectedBaseStations.value.length);
		return {
			selectedBaseStationsCheckboxes,
			selectedBaseStationsDisplay,
			nonSelectedBaseStationsCheckboxes,
			nonSelectedBaseStationsDisplay,
			selectAllChecked1,
			selectAllChecked2,
			searchQuery,
			onSelect,
			onDeselect,
			isSubmitting,
			onSubmit,
			selectedBaseStationsCount,
		};
	},
}
</script>

<style scoped>
.mover-group-wrapper {
	display: grid;
	grid-template-columns: 1fr auto 1fr;
}
.checkbox-group-title {
	margin-bottom: 8px;
}
.checkbox-group {
	height: 300px;
}
.select-all-checkbox {
	margin: 8px 9px;
}
.buttons-wrapper {
	width: auto;
	margin: auto 8px;
}
.include-in-vrs-tag {
	line-height: 14px;
	padding-inline: 2px;
}
</style>